import { db } from "../../../firebase/firebase";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";

const companyKey = (company) =>
  String(company || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const safeId = (value) =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .slice(0, 180);

export async function saveDailyBookingPortalIncome({
  rows,
  company,
  mappings,
  sourceFileName,
  uploadedBy = "",
}) {
  if (!company) throw new Error("Company is required.");
  if (!Array.isArray(rows) || !rows.length) throw new Error("No Booking Portal rows to save.");

  const cKey = companyKey(company);
  const grouped = new Map();
  rows.forEach((row) => {
    const date = String(row.reportDate || "").trim();
    if (!date) return;
    if (!grouped.has(date)) grouped.set(date, []);
    grouped.get(date).push(row);
  });

  const results = [];

  for (const [reportDate, dateRows] of grouped.entries()) {
    const uploadKey = `${reportDate}__${cKey}__booking-portal`;
    const recordsRef = collection(db, "dailyBookingPortalEarnings", reportDate, "records");
    const existingSnap = await getDocs(recordsRef);
    const oldDocs = existingSnap.docs.filter((d) => {
      const data = d.data() || {};
      return String(data.companyKey || "") === cKey;
    });

    const batch = writeBatch(db);
    oldDocs.forEach((old) => batch.delete(old.ref));

    let savedCount = 0;
    let bankTotal = 0;
    let cashTotal = 0;

    dateRows.forEach((row, index) => {
      const internalDriverId = mappings[row.driver_id];
      if (!internalDriverId) return;

      const bankIncome = Number(row.bank_income || 0);
      const cashIncome = Number(row.cash_income || 0);
      const recordId = safeId(`${cKey}__${internalDriverId}__${index}`);

      batch.set(doc(recordsRef, recordId), {
        reportDate,
        company,
        incomeCompany: company,
        companyKey: cKey,
        sourceType: "booking_portal",
        driverId: internalDriverId,
        driverName: row.name || "",
        sourceDriverName: row.name || "",
        sourceDriverId: row.driver_id || "",
        bankIncome,
        cashIncome,
        sourceFileName: sourceFileName || "",
        uploadKey,
        updatedAt: serverTimestamp(),
      });

      savedCount += 1;
      bankTotal += bankIncome;
      cashTotal += cashIncome;
    });

    await batch.commit();

    await setDoc(
      doc(db, "dailyBookingPortalUploads", uploadKey),
      {
        uploadKey,
        reportDate,
        company,
        incomeCompany: company,
        companyKey: cKey,
        sourceType: "booking_portal",
        sourceFileName: sourceFileName || "",
        rowCount: savedCount,
        bankTotal,
        cashTotal,
        uploadedBy: uploadedBy || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    results.push({
      reportDate,
      savedCount,
      bankTotal,
      cashTotal,
      uploadKey,
      replacedCount: oldDocs.length,
    });
  }

  return results.sort((a, b) => a.reportDate.localeCompare(b.reportDate));
}
